import axiosInstance from 'services/api/config/api';
import type {
    GetAccessManagerDataResponse,
    CreateTenantRequest,
    Tenant,
    DeleteResponse,
    ActiveAccount,
    OutboundTrunkInfo,
    WorkflowSummary,
    MaskedTenantConfigResponse,
    MembersResponse,
    Member,
    CreateMemberRequest,
    UpdateMemberRequest,
    DeleteMemberResponse,
    CompaniesResponse,
    Product,
} from 'types/accessManager.type';

export const getAccessManagerData = (): Promise<GetAccessManagerDataResponse> => {
    return axiosInstance.get<GetAccessManagerDataResponse>('/access-manager').then((res) => res.data);
};

export const createTenant = (data: CreateTenantRequest): Promise<Tenant> => {
    return axiosInstance.post<Tenant>('/access-manager/tenant', data).then((res) => res.data);
};

export const deleteTenant = (id: string): Promise<DeleteResponse> => {
    return axiosInstance.delete<DeleteResponse>(`/access-manager/tenant/${id}`).then((res) => res.data);
};

export const updateTenantStatus = (id: string): Promise<Tenant> => {
    return axiosInstance.put<Tenant>(`/access-manager/tenant/status/${id}`).then((res) => res.data);
};

export const getActiveAccounts = (): Promise<ActiveAccount[]> => {
    return axiosInstance.get<ActiveAccount[]>('/access-manager/twilio/active-accounts').then((res) => res.data);
};

export const getOutboundTrunks = (): Promise<OutboundTrunkInfo[]> => {
    return axiosInstance.get<OutboundTrunkInfo[]>('/access-manager/livekit/outbound-trunks').then((res) => res.data);
};

export const getTenantWorkflows = (): Promise<WorkflowSummary[]> => {
    return axiosInstance
        .get<{ workflows: WorkflowSummary[] }>('/access-manager/workflows')
        .then((res) => res.data.workflows);
};

export const updateUserWorkflowAssignments = (id: string, workflow_ids: string[]): Promise<WorkflowSummary[]> => {
    return axiosInstance
        .put<{ workflow_assignments: WorkflowSummary[] }>(`/access-manager/users/${id}/workflow-assignments`, {
            workflow_ids,
        })
        .then((res) => res.data.workflow_assignments);
};

export const getTenantConfig = (id: string): Promise<MaskedTenantConfigResponse> => {
    return axiosInstance.get<MaskedTenantConfigResponse>(`/access-manager/tenant/${id}/config`).then((res) => res.data);
};

// ---- User management by product (/api/v1/members) ----
// accountId: the company a super admin manages; omitted, the caller's own.

const accountParams = (accountId?: string) => (accountId ? { params: { account_id: accountId } } : {});

// The writes below fail into a modal that shows the backend's own message
// (memberErrorMessage); the global error toast would say it a second time.
const ownErrorMessage = { suppressErrorToast: true } as const;

export const getMembers = (accountId?: string): Promise<MembersResponse> => {
    return axiosInstance.get<MembersResponse>('/v1/members', accountParams(accountId)).then((res) => res.data);
};

export const createMember = ({
    accountId,
    data,
}: {
    accountId?: string;
    data: CreateMemberRequest;
}): Promise<Member> => {
    return axiosInstance
        .post<Member>('/v1/members', data, { ...accountParams(accountId), ...ownErrorMessage })
        .then((res) => res.data);
};

export const inviteMember = ({
    accountId,
    data,
}: {
    accountId?: string;
    data: CreateMemberRequest;
}): Promise<Member> => {
    return axiosInstance
        .post<Member>('/v1/members/invite', data, { ...accountParams(accountId), ...ownErrorMessage })
        .then((res) => res.data);
};

export const updateMember = ({
    accountId,
    id,
    data,
}: {
    accountId?: string;
    id: string;
    data: UpdateMemberRequest;
}): Promise<Member> => {
    return axiosInstance
        .put<Member>(`/v1/members/${id}`, data, { ...accountParams(accountId), ...ownErrorMessage })
        .then((res) => res.data);
};

export const deleteMember = ({ accountId, id }: { accountId?: string; id: string }): Promise<DeleteMemberResponse> => {
    return axiosInstance
        .delete<DeleteMemberResponse>(`/v1/members/${id}`, { ...accountParams(accountId), ...ownErrorMessage })
        .then((res) => res.data);
};

// ---- Companies and their products (SLAI staff) ----

export const getCompanies = (): Promise<CompaniesResponse> => {
    return axiosInstance.get<CompaniesResponse>('/v1/accounts').then((res) => res.data);
};

export const setCompanyProduct = ({
    accountId,
    product,
    enabled,
}: {
    accountId: string;
    product: Product;
    enabled: boolean;
}): Promise<void> => {
    return axiosInstance.put(`/v1/accounts/${accountId}/products/${product}`, { enabled }).then(() => undefined);
};

// Makes a person of the company its one owner (SLAI staff). The previous owner
// keeps their own roles and is no longer the owner.
export const setCompanyOwner = ({ accountId, userId }: { accountId: string; userId: string }): Promise<void> => {
    return axiosInstance
        .put(`/v1/accounts/${accountId}/owner`, { user_id: userId }, ownErrorMessage)
        .then(() => undefined);
};
